import { CheckCircleIcon } from '@heroicons/vue/24/solid';

import { pacePeriods } from './pace-periods';
import { reviewTarget } from './settings-review';
import { JOURNEY_STAGES } from './stages';
import { interviewTime, practiceLink } from './upcoming-interview';

// The Resume state and its diagnosis (EN-1005) show the same chip and open the same resume. The score
// stands alone, as in ResumAI (EN-941).
const resumeChip = ({ focus }) => (focus.resume.score === null
	? 'Resume: Not scored yet'
	: `Resume: ${focus.resume.score}`);

const resumeShortChip = ({ focus }) => (focus.resume.score === null
	? 'Not Scored'
	: `${focus.resume.score}`);

// The Interviews chip shows the stage's own track metric rather than a format of its own. A choice
// needs the metric, but the interview it counts can be moved back to another status before the window ends.
const interviewsMetric = ({ stages }) => stages.find(({ key }) => key === 'interviews')?.metric ?? 'Waiting';

const improveResume = ({ focus }) => ({
	label: 'Improve Resume',
	to: { name: 'resumes.edit', params: { id: focus.resume.resume } },
});

/**
 * What the header button and its popover show for each Your Journey focus state (EN-1024): the
 * button's text, its shorter text on a phone (`shortChip`, EN-1162: the metric alone, or null for the
 * icon alone), and the popover's headline, condensed detail, primary action and the page that action
 * opens.
 *
 * Keyed by `focus.state`, or by `focus.stage` for a user's choice, which has no state. A choice of
 * Interviews with an interview ahead is keyed `upcoming_interview` instead (see `focusStateFor()`). A
 * state shows no header button until its own ticket adds it here.
 *
 * Every function takes the `GET /api/journey` payload. `headline` and `action` repeat the widget's
 * own, including the "You're focusing on …" headline a user's choice gets (EN-1023); each widget's
 * test checks they still match. `detail` returns parts so the popover can set
 * the count in medium weight, as the design does. `isOwnPage` takes the current route and the
 * payload: on the page the action opens, the popover offers only See Your Journey.
 */
// Rule 10 (state `application`) and a user's own choice of Application (no state) share this entry.
// The widget's primary action is AutoApply on a job it reads from the Job Board itself, which the
// header doesn't have, so the popover offers the widget's other action instead.
const APPLICATION = {
	isShown: () => true,
	chip: ({ metrics }) => `Applications: ${metrics.sent_today}/${metrics.daily_target}`,
	shortChip: ({ metrics }) => `${metrics.sent_today}/${metrics.daily_target}`,
	headline: ({ focus }) => (focus.source === 'user_choice'
		? 'You\'re focusing on your applications today'
		: 'You\'re ready to apply!'),
	detail: ({ metrics }) => [
		{ text: 'You\'ve sent ' },
		{ text: `${metrics.sent_today} of ${metrics.daily_target}`, strong: true },
		{ text: ' applications today. One high match job is ready.' },
	],
	action: () => ({ label: 'View All Jobs', to: { name: 'job-board-search', query: { autoApplyFilter: 1 } } }),
	isOwnPage: (route) => route.name === 'job-board-search',
};

export const FOCUS_STATES = {
	profile: {
		// The widget renders only with `focus.profile`.
		isShown: ({ focus }) => Boolean(focus.profile),
		chip: ({ focus }) => `Profile: ${focus.profile.completed}/${focus.profile.total}`,
		shortChip: ({ focus }) => `${focus.profile.completed}/${focus.profile.total}`,
		headline: ({ focus }) => (focus.source === 'user_choice'
			? 'You\'re focusing on your profile today'
			: 'Let\'s finish your profile first'),
		detail: ({ focus }) => (focus.profile.remaining === 1
			? [
				{ text: '1 field', strong: true },
				{ text: ' is missing or needs a quick check. Your matches and AutoApply forms depend on it.' },
			]
			: [
				{ text: `${focus.profile.remaining} fields`, strong: true },
				{ text: ' are missing or need a quick check. Your matches and AutoApply forms depend on them.' },
			]),
		action: () => ({ label: 'Complete Profile', to: { name: 'profile' } }),
		isOwnPage: (route) => route.name === 'profile',
	},
	resume: {
		// The widget renders only with `focus.resume`, which needs a primary resume.
		isShown: ({ focus }) => Boolean(focus.resume),
		chip: resumeChip,
		shortChip: resumeShortChip,
		headline: ({ focus }) => (focus.source === 'user_choice'
			? 'You\'re focusing on your resume today'
			: 'Let\'s strengthen your resume first'),
		detail: ({ focus }) => {
			const { score, max_score: maxScore, ready_score: readyPercent, improvements } = focus.resume;

			if (score === null) {
				return [{ text: 'Open your resume in ResumAI to see your score and what to improve.' }];
			}

			// `ready_score` is a percent of the resume's own maximum; quote it in the points the button shows.
			const readyScore = Math.ceil(readyPercent * maxScore / 100);

			if (!improvements) {
				return [{ text: `Resumes scoring ${readyScore}+ land noticeably more interviews.` }];
			}

			const fixes = { text: improvements === 1 ? '1 fix' : `${improvements} fixes`, strong: true };

			// A user can choose Resume once it's already ready.
			return score >= readyScore
				? [fixes, { text: ' in ResumAI could raise your score further.' }]
				: [fixes, { text: ` in ResumAI could push your score past ${readyScore}.` }];
		},
		action: improveResume,
		// The builder has no app header, so the popover can't open there today.
		isOwnPage: (route) => route.name === 'resumes.edit',
	},
	resume_blocking_applications: {
		// Rule 3 (EN-1005). The widget renders only with `focus.resume`, which carries the diagnosis. The
		// button turns orange for any diagnosis on its own.
		isShown: ({ focus }) => Boolean(focus.resume?.diagnosis),
		chip: resumeChip,
		shortChip: resumeShortChip,
		headline: () => 'Your resume may be holding back your applications',
		detail: ({ focus }) => {
			const { applications, interviewed, maturation_days: days } = focus.resume.diagnosis;

			return interviewed === 0
				? [
					{ text: 'None of the ' },
					{ text: `${applications} applications`, strong: true },
					{ text: ` you sent more than ${days} days ago have led to an interview yet.` },
				]
				: [
					{ text: 'Only ' },
					{ text: `${interviewed} of the ${applications} applications`, strong: true },
					{ text: ` you sent more than ${days} days ago have led to an interview.` },
				];
		},
		action: improveResume,
		// The builder has no app header, so the popover can't open there today.
		isOwnPage: (route) => route.name === 'resumes.edit',
	},
	application: APPLICATION,
	// The Pace dropped diagnosis (EN-1006) keeps Application's button, action and page and changes
	// only its copy. The header button turns orange for any diagnosis.
	pace_dropped: {
		...APPLICATION,
		headline: () => 'Your application pace has slowed down',
		detail: ({ metrics }) => {
			const period = pacePeriods(metrics.pace_window_days);

			return [
				{ text: 'Only ' },
				{ text: `${metrics.pace_recent_daily_average} a day`, strong: true },
				{ text: ` ${period.recent}, down from ${metrics.pace_previous_daily_average} ${period.previous}. One high match job is ready.` },
			];
		},
	},
	// Rule 2 (EN-1000). The widget renders only with `focus.settings`. The button turns orange for any
	// diagnosis on its own. The widget's primary action is its inline form, which only Your Journey
	// has, so the popover offers Review Full Profile, and See Your Journey for the form.
	settings_mismatch: {
		isShown: ({ focus }) => Boolean(focus.settings),
		chip: ({ metrics }) => {
			const count = metrics.settings_mismatch_fields.length;

			return count ? `Profile: ${count} to review` : 'Profile: Review settings';
		},
		shortChip: ({ metrics }) => {
			const count = metrics.settings_mismatch_fields.length;

			return count ? `${count} to review` : 'Review';
		},
		headline: () => 'You\'re applying to different jobs than we recommend',
		detail: ({ metrics }) => [
			{
				text: `${metrics.settings_mismatch_mismatched} of your last ${metrics.settings_mismatch_applications} applications`,
				strong: true,
			},
			{ text: ' were for jobs outside your Recommendation Settings.' },
		],
		action: ({ metrics }) => ({ label: 'Review Full Profile', to: reviewTarget(metrics) }),
		// Whichever page the action opens: the Job Board's Recommendation Settings deep link only opens
		// the panel on mount, so a link to it from the Job Board itself would do nothing.
		isOwnPage: (route, { metrics }) => route.name === reviewTarget(metrics).name,
	},
	// The practice prompt (EN-1023): a user's choice of Interviews with no interview ahead. The widget
	// renders it only with `focus.interviews`.
	interviews: {
		isShown: ({ focus }) => Boolean(focus.interviews),
		chip: (journey) => `Interviews: ${interviewsMetric(journey)}`,
		shortChip: interviewsMetric,
		headline: ({ focus }) => (focus.interviews.has_unscheduled_interview
			? 'There\'s always time to practice'
			: 'No interview lined up, but there\'s always time to practice'),
		detail: ({ focus }) => {
			const score = focus.interviews.last_practice_score;

			if (score === null) {
				return [{
					text: 'Practice covers Content, Speech, and Non-verbals, '
						+ 'the same categories real interviewers judge you on.',
				}];
			}

			return [
				{ text: 'Your last practice scored ' },
				{ text: `${score}/100`, strong: true },
				{ text: '. Practice covers Content, Speech, and Non-verbals.' },
			];
		},
		action: () => ({ label: 'Practice Interview', to: { name: 'interview-ai-create' } }),
		isOwnPage: (route) => route.name === 'interview-ai-create',
	},
	// Rule 1 (EN-1030), and a user's choice of Interviews while an interview is ahead, which
	// focusStateFor() sends here instead of to the practice prompt. The widget renders it only with
	// `focus.interviews.upcoming`.
	upcoming_interview: {
		isShown: ({ focus }) => Boolean(focus.interviews?.upcoming),
		chip: ({ focus }) => `Interview: ${interviewTime(focus.interviews.upcoming.scheduled_at).short}`,
		shortChip: ({ focus }) => interviewTime(focus.interviews.upcoming.scheduled_at).short,
		headline: ({ focus }) => (focus.source === 'user_choice'
			? 'You\'re focusing on your upcoming interview today'
			: `Your ${focus.interviews.upcoming.company} interview is ${interviewTime(focus.interviews.upcoming.scheduled_at).when}`),
		detail: ({ focus }) => [
			{ text: `${focus.interviews.upcoming.job_title}, ` },
			{ text: interviewTime(focus.interviews.upcoming.scheduled_at).full, strong: true },
			{ text: '. Run a practice round in InterviewAI first.' },
		],
		action: ({ focus }) => ({ label: 'Practice for This Interview', to: practiceLink(focus.interviews.upcoming) }),
		isOwnPage: (route) => route.name === 'interview-ai-create',
	},
	// The "Interviews without offers" diagnosis (EN-1007). The widget renders it only with
	// `focus.interviews.diagnosis`. The chip is the track's orange offer rate (PRD section 6.5), and the
	// button turns orange for any diagnosis on its own. focusStateFor() keys on the state first, so an
	// interview further ahead (`upcoming`) doesn't take the entry over.
	interviews_without_offers: {
		isShown: ({ focus }) => Boolean(focus.interviews?.diagnosis),
		chip: interviewsMetric,
		shortChip: interviewsMetric,
		headline: () => 'You\'re getting interviews. Let\'s turn them into offers.',
		detail: ({ focus }) => {
			const { interviews: count, window_days: days, practice, target_score: target } = focus.interviews.diagnosis;
			const interviews = { text: `${count} ${count === 1 ? 'interview' : 'interviews'}`, strong: true };
			const noOffer = ` in the last ${days} days and no offer yet. `;

			return practice
				? [interviews, { text: `${noOffer}Your practice score is ${practice.score}/100; aim for ${target}.` }]
				: [interviews, { text: `${noOffer}A practice interview shows exactly where to improve.` }];
		},
		action: ({ focus }) => {
			const last = focus.interviews.diagnosis.last_interview;

			return { label: 'Practice Interview', to: last ? practiceLink(last) : { name: 'interview-ai-create' } };
		},
		isOwnPage: (route) => route.name === 'interview-ai-create',
	},
	all_clear: {
		isShown: () => true,
		// All Clear has no stage: the widget's label, and a check in place of a stage icon.
		label: 'All set for today',
		icon: CheckCircleIcon,
		chip: () => 'No Updates',
		// `No Updates` doesn't fit beside the bell on a phone; the button keeps it as its name.
		shortChip: () => null,
		headline: () => 'Nothing\'s holding you back today',
		detail: () => [{ text: 'Nothing needs your attention right now.' }],
		action: () => ({ label: 'Keep Applying', to: { name: 'job-board-search', query: { autoApplyFilter: 1 } } }),
		isOwnPage: (route) => route.name === 'job-board-search',
	},
};

/**
 * The entry for the focus Your Journey displays, or null when the header has nothing to show.
 */
export const focusStateFor = (journey) => {
	const focus = journey?.focus;

	if (!focus) {
		return null;
	}

	// A user's choice has no state. A choice of Interviews shows the interview ahead if there is one,
	// else the practice prompt (EN-1030).
	const key = focus.state ?? (focus.interviews?.upcoming ? 'upcoming_interview' : focus.stage);
	const entry = FOCUS_STATES[key];

	return entry?.isShown(journey) ? entry : null;
};

/**
 * The icon the header button and its popover show for the focus: the entry's own, or its stage's.
 * All Clear has no stage and brings its own check.
 */
export const focusIconFor = (journey) => {
	const entry = focusStateFor(journey);

	if (!entry) {
		return null;
	}

	return entry.icon ?? JOURNEY_STAGES.find(({ key }) => key === journey.focus.stage)?.icon ?? null;
};
